// src/components/common/Input.tsx
import React, { InputHTMLAttributes, forwardRef, useState } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import CountryCodeSelector from './CountryCodeSelector';

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  variant?: 'default' | 'glass';
  withCountryCode?: boolean;
  countryCode?: string;
  onCountryCodeChange?: (code: string) => void;
}

const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  leftIcon,
  rightIcon,
  helperText,
  size = 'md',
  fullWidth = false,
  variant = 'glass',
  className = '',
  id,
  type = 'text',
  withCountryCode = false,
  countryCode = '+91',
  onCountryCodeChange,
  ...rest
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const inputId = id || `input-${Math.random().toString(36).substring(2, 11)}`;
  
  // Handle width
  const widthClass = fullWidth ? 'w-full' : '';
  
  // Input type to display
  const inputType = type === 'password' ? (showPassword ? 'text' : 'password') : type;
  
  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };
  
  // Check if input is for phone and has country code
  const isPhoneWithCountryCode = type === 'tel' && withCountryCode;
  
  // Set standard input height based on size
  const getInputHeight = () => {
    switch (size) {
      case 'sm': return 'h-[38px]';
      case 'lg': return 'h-[46px]';
      case 'md':
      default: return 'h-[42px]';
    }
  };
  
  const inputHeight = getInputHeight();
  
  return (
    <div className={`${widthClass}`}>
      {label && (
        <label htmlFor={inputId} className="block mb-2 text-sm font-medium text-white">
          {label}
        </label>
      )}
      
      {/* Phone with country code */}
      {isPhoneWithCountryCode ? (
        <div className={`flex gap-2 ${inputHeight}`}>
          {/* Country code selector */}
          <div className="w-24 flex-shrink-0 h-full">
            <CountryCodeSelector 
              value={countryCode}
              onChange={onCountryCodeChange || (() => {})}
              disabled={rest.disabled}
              variant={variant}
            />
          </div>
          
          {/* Phone input field */}
          <div className={`
            flex-grow relative h-full
            ${variant === 'glass' ? 'glass-input' : 'bg-gray-800 border border-gray-700 text-white rounded-lg'} 
            ${error ? 'border-red-500' : ''}
            ${className}
            flex items-center
          `}>
            {/* Left Icon placed first */}
            {leftIcon && (
              <div className="flex-shrink-0 ml-3 mr-1 text-white/50">
                {leftIcon}
              </div>
            )}
            
            {/* Input with no border/background */}
            <input
              ref={ref}
              id={inputId}
              type={inputType}
              className={`
                flex-grow bg-transparent border-none focus:outline-none focus:ring-0
                py-2 px-0
                ${size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-lg' : 'text-base'}
                placeholder-white/50
              `}
              aria-invalid={error ? 'true' : 'false'}
              {...rest}
            />
            
            {/* Right Icon */}
            {rightIcon && (
              <div className="flex-shrink-0 mr-3 text-white/50">
                {rightIcon}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Regular input without country code */
        <div className={`
          relative ${inputHeight}
          ${variant === 'glass' ? 'glass-input' : 'bg-gray-800 border border-gray-700 text-white rounded-lg'} 
          ${error ? 'border-red-500' : ''}
          ${widthClass}
          ${className}
          flex items-center
        `}>
          {/* Left Icon placed first */}
          {leftIcon && (
            <div className="flex-shrink-0 ml-3 mr-1 text-white/50">
              {leftIcon}
            </div>
          )}
          
          {/* Input with no border/background */}
          <input
            ref={ref}
            id={inputId}
            type={inputType}
            className={`
              flex-grow bg-transparent border-none focus:outline-none focus:ring-0
              py-2 px-0
              ${size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-lg' : 'text-base'}
              placeholder-white/50
            `}
            aria-invalid={error ? 'true' : 'false'}
            {...rest}
          />
          
          {/* Password Toggle Icon */}
          {type === 'password' && (
            <button 
              type="button" 
              onClick={togglePasswordVisibility}
              className="flex-shrink-0 mr-3 text-white/50 focus:outline-none"
            >
              {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
            </button>
          )}
          
          {/* Right Icon */}
          {rightIcon && type !== 'password' && (
            <div className="flex-shrink-0 mr-3 text-white/50">
              {rightIcon}
            </div>
          )}
        </div>
      )}
      
      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-red-500">
          {error}
        </p>
      )}
      
      {/* Helper Text */}
      {helperText && !error && (
        <p className="mt-1 text-sm text-white/60">
          {helperText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';
export default Input;
